import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useGroup } from "@/context/GroupContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { CardSkeleton } from "@/components/Skeletons";
import CategoryManager from "@/components/CategoryManager";
import { Shield, Trash2, Users2, Tag, ShieldCheck, FileText, RotateCcw, Headphones, ExternalLink } from "lucide-react";

export default function Settings() {
  const { group, groupId, isAdmin, refresh } = useGroup();
  const [tab, setTab] = useState("group");

  if (!groupId) return <CardSkeleton />;

  return (
    <div className="space-y-5 lg:max-w-4xl" data-testid="settings-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">
          {isAdmin ? `Admin • ${group?.name}` : group?.name}
        </div>
        <h1 className="heading text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mt-1">Settings</h1>
        <p className="text-sm text-slate-500 mt-1">
          {isAdmin
            ? "Manage your group's details and expense categories."
            : "Read-only view. Only the group admin can make changes."}
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab} data-testid="settings-tabs">
        <TabsList className="h-11 rounded-full bg-slate-100 p-1 w-full max-w-md">
          <TabsTrigger
            value="group"
            className="flex-1 rounded-full data-[state=active]:bg-white data-[state=active]:shadow"
            data-testid="settings-tab-group"
          >
            <Users2 className="w-3.5 h-3.5 mr-1.5" /> Group settings
          </TabsTrigger>
          <TabsTrigger
            value="categories"
            className="flex-1 rounded-full data-[state=active]:bg-white data-[state=active]:shadow"
            data-testid="settings-tab-categories"
          >
            <Tag className="w-3.5 h-3.5 mr-1.5" /> Categories
          </TabsTrigger>
        </TabsList>

        <TabsContent value="group" className="mt-5">
          <GroupSettingsPanel group={group} groupId={groupId} isAdmin={isAdmin} refresh={refresh} />
        </TabsContent>

        <TabsContent value="categories" className="mt-5">
          <div className="rounded-3xl p-5 bg-white border border-slate-200">
            <CategoryManager groupId={groupId} isAdmin={isAdmin} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function GroupSettingsPanel({ group, groupId, isAdmin, refresh }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (group) {
      setName(group.name);
      setDescription(group.description || "");
    }
  }, [group]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/groups/${groupId}`, { name, description });
      toast.success("Group updated");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!window.confirm(`Delete "${group.name}" and all its data? This cannot be undone.`)) return;
    try {
      await api.delete(`/groups/${groupId}`);
      toast.success("Group deleted");
      navigate("/");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-4">
      {!isAdmin && (
        <div className="rounded-3xl bg-amber-50 border border-amber-200 p-4 flex items-start gap-3">
          <Shield className="w-4 h-4 text-amber-600 mt-0.5" />
          <div className="text-xs text-amber-800">Only the admin can edit group details.</div>
        </div>
      )}

      <div className="bg-white rounded-3xl p-5 border border-slate-200 space-y-4">
        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Name</Label>
          <Input disabled={!isAdmin} value={name} onChange={(e) => setName(e.target.value)} className="mt-2 h-12 rounded-2xl" data-testid="group-name-input" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Group link</Label>
          <div className="mt-2 h-12 rounded-2xl bg-slate-50 border border-slate-200 px-4 flex items-center text-sm text-slate-500 font-mono">
            {window.location.host}/<b className="text-slate-900">{group?.slug}</b>
          </div>
          <p className="text-xs text-slate-500 mt-1.5">Group slugs can't be changed once created.</p>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Description</Label>
          <Textarea disabled={!isAdmin} value={description} onChange={(e) => setDescription(e.target.value)} className="mt-2 rounded-2xl" data-testid="group-description-input" />
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Button disabled={busy} onClick={save} className="rounded-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="group-save-button">
              {busy ? "Saving…" : "Save changes"}
            </Button>
            <Button
              onClick={del}
              variant="outline"
              className="rounded-full border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
              data-testid="group-delete-button"
            >
              <Trash2 className="w-4 h-4 mr-1.5" /> Delete group
            </Button>
          </div>
        )}
      </div>

      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="heading text-lg font-semibold text-slate-900">Members ({group?.members?.length || 0})</h2>
        </div>
        <div className="space-y-2">
          {group?.members?.map((m) => (
            <div key={m.id} className="flex items-center gap-3 p-3 rounded-2xl bg-white border border-slate-200">
              <div className="w-10 h-10 rounded-full overflow-hidden bg-slate-100">
                {m.photo && <img src={m.photo} alt={m.name} className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-semibold text-slate-900">{m.name}</div>
                  {group.admin_id === m.id && (
                    <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">Admin</span>
                  )}
                </div>
                <div className="text-xs text-slate-500 truncate">{m.email}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Legal & Support Section */}
      <section className="bg-white rounded-3xl p-5 border border-slate-200 space-y-4">
        <div>
          <h2 className="heading text-lg font-bold text-slate-900">Legal & Support</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Review user agreements, privacy policies, terms, and customer support channels.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
          <Link
            to="/privacy-policy"
            className="group flex items-center gap-3 p-3.5 rounded-2xl bg-slate-50 hover:bg-slate-100 border border-slate-200/80 transition-all duration-200"
            data-testid="settings-privacy-link"
          >
            <div className="w-9 h-9 rounded-xl bg-emerald-100 text-emerald-700 grid place-items-center group-hover:scale-105 transition-transform">
              <ShieldCheck className="w-4.5 h-4.5" />
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-900 group-hover:text-slate-800">Privacy Policy</div>
              <div className="text-[11px] text-slate-500">Data protection & security</div>
            </div>
          </Link>

          <Link
            to="/terms"
            className="group flex items-center gap-3 p-3.5 rounded-2xl bg-slate-50 hover:bg-slate-100 border border-slate-200/80 transition-all duration-200"
            data-testid="settings-terms-link"
          >
            <div className="w-9 h-9 rounded-xl bg-blue-100 text-blue-700 grid place-items-center group-hover:scale-105 transition-transform">
              <FileText className="w-4.5 h-4.5" />
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-900 group-hover:text-slate-800">Terms & Conditions</div>
              <div className="text-[11px] text-slate-500">User agreement & usage rules</div>
            </div>
          </Link>

          <Link
            to="/refund-policy"
            className="group flex items-center gap-3 p-3.5 rounded-2xl bg-slate-50 hover:bg-slate-100 border border-slate-200/80 transition-all duration-200"
            data-testid="settings-refund-link"
          >
            <div className="w-9 h-9 rounded-xl bg-amber-100 text-amber-700 grid place-items-center group-hover:scale-105 transition-transform">
              <RotateCcw className="w-4.5 h-4.5" />
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-900 group-hover:text-slate-800">Refund Policy</div>
              <div className="text-[11px] text-slate-500">Subscription & billing terms</div>
            </div>
          </Link>

          <a
            href="https://saharey.com/contactus"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-3 p-3.5 rounded-2xl bg-slate-50 hover:bg-slate-100 border border-slate-200/80 transition-all duration-200"
            data-testid="settings-contact-link"
          >
            <div className="w-9 h-9 rounded-xl bg-indigo-100 text-indigo-700 grid place-items-center group-hover:scale-105 transition-transform">
              <Headphones className="w-4.5 h-4.5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1 text-sm font-semibold text-slate-900 group-hover:text-slate-800">
                <span>Contact Us</span>
                <ExternalLink className="w-3 h-3 text-slate-400" />
              </div>
              <div className="text-[11px] text-slate-500">Help center & support desk</div>
            </div>
          </a>
        </div>
      </section>
    </div>
  );
}
